// ConnectionsAuditTab — N3.1 (relatório permanente das Conexões Bíblicas).
// Consome /api/admin/connections-audit?days=30 e apresenta:
//   - cobertura da janela (dias com/sem registro)
//   - frequência por referência (livro+capítulo) + alertas
//   - frequência por livro + alertas
//   - combinações exatas repetidas
//   - papéis teológicos (Fundamento/Desenvolvimento/Aplicação)
//   - distribuição AT × NT
//   - lista dia a dia com tema e conexões
//   - referências inválidas e rotas quebradas (esperamos zero)
//
// Somente leitura — nenhuma referência é alterada.
import React, { useEffect, useState } from "react";
import { AlertTriangle, RefreshCw, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";

const ROLE_LABEL = {
  fundamento: "Fundamento",
  desenvolvimento: "Desenvolvimento",
  aplicacao: "Aplicação",
};

const ROLE_COLOR = {
  fundamento: "bg-navy/10 text-navy",
  desenvolvimento: "bg-gold/15 text-gold",
  aplicacao: "bg-emerald-100 text-emerald-800",
};

export default function ConnectionsAuditTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (d = days) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/admin/connections-audit`, { params: { days: d } });
      setData(r?.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Falha ao carregar");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6" data-testid="admin-connections-audit-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Editorial</p>
          <h2 className="font-display text-3xl text-navy mt-1">Auditoria de Conexões</h2>
          <p className="text-sm text-navy/65 mt-2 max-w-2xl leading-relaxed">
            Relatório permanente das Conexões Bíblicas dos últimos dias. Detecta
            repetição de capítulo, livro e combinações exatas, além dos papéis
            teológicos e distribuição AT × NT. Somente leitura, não altera
            nenhuma referência.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-widest text-navy/60">Janela</label>
          <select
            value={days}
            onChange={(e) => { const d = parseInt(e.target.value, 10); setDays(d); load(d); }}
            className="border border-border rounded-full px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gold"
            data-testid="audit-window-select"
          >
            <option value={7}>7 dias</option>
            <option value={14}>14 dias</option>
            <option value={30}>30 dias</option>
            <option value={60}>60 dias</option>
            <option value={90}>90 dias</option>
          </select>
          <button
            type="button"
            onClick={() => load(days)}
            className="inline-flex items-center gap-2 border border-border rounded-full px-3 py-1.5 text-xs uppercase tracking-widest text-navy hover:text-gold hover:border-gold transition"
            data-testid="audit-refresh"
            disabled={loading}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Carregando…" : "Atualizar"}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="audit-error">
          Erro ao carregar: {error}
        </div>
      )}

      {data && !loading && (
        <>
          {/* Resumo + alertas */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <SummaryCard
              label="Janela"
              value={`${data.coverage.with_records}/${data.window.days}`}
              hint={`${data.window.start_date} → ${data.window.end_date}`}
              testid="audit-summary-window"
            />
            <SummaryCard
              label="AT × NT"
              value={`${data.testament_distribution.at_pct}% / ${data.testament_distribution.nt_pct}%`}
              hint={`AT ${data.testament_distribution.at} • NT ${data.testament_distribution.nt}`}
              testid="audit-summary-testament"
            />
            <SummaryCard
              label="Combos repetidos"
              value={String(data.repeated_combos.length)}
              hint={data.alerts.has_repeated_combos ? "Precisa de atenção" : "Nenhum"}
              alert={data.alerts.has_repeated_combos}
              testid="audit-summary-combos"
            />
            <SummaryCard
              label="Referências inválidas"
              value={String(data.invalid_references.length)}
              hint={data.alerts.has_invalid_references ? "Corrigir agora" : "Todas válidas"}
              alert={data.alerts.has_invalid_references}
              testid="audit-summary-invalid"
            />
          </div>

          {/* Papéis teológicos */}
          <Card title="Papéis teológicos (últimos dias)" testid="audit-roles">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <RoleTile label="Fundamento" value={data.role_distribution.fundamento} color="bg-navy/10 text-navy" />
              <RoleTile label="Desenvolvimento" value={data.role_distribution.desenvolvimento} color="bg-gold/15 text-gold" />
              <RoleTile label="Aplicação" value={data.role_distribution.aplicacao} color="bg-emerald-100 text-emerald-800" />
              <RoleTile label="Sem papel (legado)" value={data.role_distribution.sem_papel} color="bg-navy/5 text-navy/60" />
            </div>
          </Card>

          {/* Combinações repetidas */}
          {data.repeated_combos.length > 0 && (
            <Card title="Combinações exatas repetidas" testid="audit-combos" alert>
              <ul className="space-y-3">
                {data.repeated_combos.map((c, i) => (
                  <li key={i} className="rounded-xl border border-red-200 bg-red-50/60 p-3">
                    <p className="text-sm text-navy font-medium">
                      <AlertTriangle className="inline w-4 h-4 mr-1 text-red-600" />
                      {c.count}× · {c.combo_pretty.join(" • ")}
                    </p>
                    <p className="text-xs text-navy/60 mt-1">Dias: {c.days.join(", ")}</p>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Frequência por referência */}
          <Card title="Frequência por referência (livro+capítulo)" testid="audit-refs">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm">
              {data.reference_frequency.map((r, i) => (
                <li
                  key={i}
                  className={`flex justify-between items-center rounded-lg px-3 py-2 ${
                    r.alert ? "bg-red-50 text-red-800 border border-red-200" : "bg-white border border-border"
                  }`}
                  data-testid={`audit-ref-${i}`}
                >
                  <span className="font-medium">{r.ref}</span>
                  <span className="text-xs uppercase tracking-widest">{r.count}×{r.alert ? " · alerta" : ""}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* Frequência por livro */}
          <Card title="Frequência por livro" testid="audit-books">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm">
              {data.book_frequency.map((b, i) => (
                <li
                  key={i}
                  className={`flex justify-between items-center rounded-lg px-3 py-2 ${
                    b.alert ? "bg-red-50 text-red-800 border border-red-200" : "bg-white border border-border"
                  }`}
                  data-testid={`audit-book-${i}`}
                >
                  <span className="font-medium">{b.book_name} <span className="text-[10px] uppercase tracking-widest text-navy/50 ml-1">{b.testament}</span></span>
                  <span className="text-xs uppercase tracking-widest">{b.count}×{b.alert ? " · alerta" : ""}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* Referências inválidas / rotas quebradas */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card title="Referências inválidas" testid="audit-invalid">
              {data.invalid_references.length === 0 ? (
                <p className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="w-4 h-4" /> Nenhuma referência inválida no período.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {data.invalid_references.map((it, i) => (
                    <li key={i} className="rounded-lg bg-red-50 border border-red-200 px-3 py-2">
                      <p className="font-medium text-red-800">{it.date_key} · {it.ref}</p>
                      <p className="text-xs text-red-700 mt-0.5">{it.reason}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <Card title="Rotas quebradas" testid="audit-routes">
              {data.broken_routes.length === 0 ? (
                <p className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="w-4 h-4" /> Todas as rotas /biblia/&lt;slug&gt;/&lt;capítulo&gt; navegam corretamente.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {data.broken_routes.map((it, i) => (
                    <li key={i} className="rounded-lg bg-red-50 border border-red-200 px-3 py-2">
                      <p className="font-medium text-red-800">{it.date_key} · {it.ref}</p>
                      <p className="text-xs text-red-700 mt-0.5">{it.reason}</p>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          {/* Timeline dia a dia */}
          <Card title="Detalhamento diário" testid="audit-timeline">
            {data.days.length === 0 ? (
              <p className="text-sm text-navy/60">Nenhum registro no período.</p>
            ) : (
              <ol className="space-y-4">
                {data.days.map((d) => (
                  <li key={d.date_key} className="rounded-xl border border-border p-4 bg-white" data-testid={`audit-day-${d.date_key}`}>
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                      <p className="font-medium text-navy">{d.date_key}</p>
                      <p className="text-xs text-navy/50 uppercase tracking-widest">{d.connections.length} conexões</p>
                    </div>
                    {d.theme && <p className="text-sm text-navy/75 mt-1">{d.theme}</p>}
                    <ul className="mt-3 space-y-1.5">
                      {d.connections.map((c, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${ROLE_COLOR[c.theological_role] || "bg-navy/5 text-navy/50"}`}>
                            {ROLE_LABEL[c.theological_role] || "sem papel"}
                          </span>
                          {c.href ? (
                            <a href={c.href} target="_blank" rel="noopener noreferrer" className="text-navy hover:text-gold underline decoration-dotted underline-offset-2">
                              {c.ref}
                            </a>
                          ) : (
                            <span className="text-navy">{c.ref}</span>
                          )}
                          <span className="text-[10px] text-navy/45 ml-auto">{c.testament}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ol>
            )}
            {data.coverage.missing_dates.length > 0 && (
              <p className="text-xs text-navy/50 mt-4">
                Dias sem cache local ({data.coverage.missing_dates.length}): {data.coverage.missing_dates.join(", ")}
              </p>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, hint, alert, testid }) {
  return (
    <div
      className={`rounded-2xl p-4 border ${alert ? "border-red-300 bg-red-50" : "border-border bg-white"}`}
      data-testid={testid}
    >
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{label}</p>
      <p className={`font-display text-2xl mt-1 ${alert ? "text-red-800" : "text-navy"}`}>{value}</p>
      <p className={`text-xs mt-1 ${alert ? "text-red-700" : "text-navy/60"}`}>{hint}</p>
    </div>
  );
}

function Card({ title, testid, alert, children }) {
  return (
    <section
      className={`rounded-2xl p-5 border ${alert ? "border-red-200 bg-red-50/40" : "border-border bg-white"}`}
      data-testid={testid}
    >
      <h3 className="font-display text-lg text-navy mb-3">{title}</h3>
      {children}
    </section>
  );
}

function RoleTile({ label, value, color }) {
  return (
    <div className="rounded-xl p-3 border border-border bg-white">
      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${color}`}>{label}</span>
      <p className="font-display text-2xl text-navy mt-2">{value}</p>
    </div>
  );
}
